import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Typography,
  Divider,
  CircularProgress,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { apiClient } from "../../../../utils/apiClient";
import { useSnackbar } from "../../../../components/SnackbarProvider";
import type { EmailTestSet, AddToTestSetParams } from "../types";

interface TestSetSelectorDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (params: AddToTestSetParams) => void;
  isLoading: boolean;
  selectedCount: number;
}

/**
 * Dialog for selecting or creating a test set
 */
export const TestSetSelectorDialog: React.FC<TestSetSelectorDialogProps> = ({
  open,
  onClose,
  onConfirm,
  isLoading,
  selectedCount,
}) => {
  const [testSets, setTestSets] = useState<EmailTestSet[]>([]);
  const [testSetsLoading, setTestSetsLoading] = useState(false);
  const [selectedTestSetId, setSelectedTestSetId] = useState<string>("");
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [newTestSetName, setNewTestSetName] = useState("");
  const [newTestSetDescription, setNewTestSetDescription] = useState("");
  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    if (open) {
      loadTestSets();
      // Reset state when opening
      setSelectedTestSetId("");
      setIsCreatingNew(false);
      setNewTestSetName("");
      setNewTestSetDescription("");
    }
  }, [open]);

  const loadTestSets = async () => {
    setTestSetsLoading(true);
    try {
      const response = await apiClient.post<{ testSets: EmailTestSet[] }>(
        "/admin/email-test-sets/list",
        {},
        { withCredentials: true }
      );
      setTestSets(response.testSets);
    } catch (error) {
      console.error("Failed to load test sets:", error);
      showSnackbar("Failed to load test sets", 3000, "error");
    } finally {
      setTestSetsLoading(false);
    }
  };

  const handleConfirm = () => {
    if (isCreatingNew) {
      if (!newTestSetName.trim()) {
        showSnackbar("Test set name is required", 3000, "error");
        return;
      }
      onConfirm({
        createNewTestSet: true,
        newTestSetName: newTestSetName.trim(),
        newTestSetDescription: newTestSetDescription.trim() || undefined,
      });
    } else {
      if (!selectedTestSetId) {
        showSnackbar("Please select a test set", 3000, "error");
        return;
      }
      onConfirm({
        testSetId: selectedTestSetId,
      });
    }
  };

  const isConfirmDisabled =
    isLoading ||
    (!isCreatingNew && !selectedTestSetId) ||
    (isCreatingNew && !newTestSetName.trim());

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Add {selectedCount} Email{selectedCount !== 1 ? "s" : ""} to Test Set
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
          disabled={isLoading}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {testSetsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {!isCreatingNew && (
              <>
                <FormControl fullWidth sx={{ mt: 1 }}>
                  <InputLabel>Select Test Set</InputLabel>
                  <Select
                    value={selectedTestSetId}
                    label="Select Test Set"
                    onChange={(e) => setSelectedTestSetId(e.target.value)}
                    disabled={isLoading}
                  >
                    {testSets.map((ts) => (
                      <MenuItem key={ts.id} value={ts.id}>
                        {ts.name} ({ts.emailCount} emails)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Divider sx={{ my: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    OR
                  </Typography>
                </Divider>

                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setIsCreatingNew(true)}
                  fullWidth
                  disabled={isLoading}
                >
                  Create New Test Set
                </Button>
              </>
            )}

            {isCreatingNew && (
              <>
                <TextField
                  fullWidth
                  label="Test Set Name"
                  value={newTestSetName}
                  onChange={(e) => setNewTestSetName(e.target.value)}
                  sx={{ mt: 1 }}
                  autoFocus
                  disabled={isLoading}
                />
                <TextField
                  fullWidth
                  label="Description (optional)"
                  value={newTestSetDescription}
                  onChange={(e) => setNewTestSetDescription(e.target.value)}
                  sx={{ mt: 2 }}
                  multiline
                  rows={2}
                  disabled={isLoading}
                />
                <Button
                  variant="text"
                  onClick={() => {
                    setIsCreatingNew(false);
                    setNewTestSetName("");
                    setNewTestSetDescription("");
                  }}
                  sx={{ mt: 1 }}
                  disabled={isLoading}
                >
                  Cancel - Select Existing Instead
                </Button>
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isConfirmDisabled}
        >
          {isLoading ? (
            <CircularProgress size={20} />
          ) : (
            `Add to Test Set`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
